import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import services from "../../services";
import { Badge, Pagination, Select } from "@mantine/core";
import { useEffect, useLayoutEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import CustomSearch from "../../components/CustomSearch/CustomSearch";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";
import toast from "react-hot-toast";
import TableSkeleton from "../../components/Skeleton/TableSkeleton";
import CustomSelect from "../../components/CustomSelect/CustomSelect";

const StorageOrders = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [id, setId] = useState(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(
    Number(searchParams.get("page")) ? Number(searchParams.get("page")) : 1
  );
  const [search, setSearch] = useState(
    searchParams.get("search") ? searchParams.get("search") : ""
  );
  const [pageSize, setPageSize] = useState(
    searchParams.get("pageSize") ? searchParams.get("pageSize") : 10
  );

  const {
    isLoading,
    error,
    data,
    isFetching,
    refetch: refetchOrders,
  } = useQuery({
    cacheTime: 0,
    queryKey: ["orders", currentPage, pageSize, search],
    enabled: !!search || (!!pageSize && !!currentPage),
    queryFn: async () =>
      await services
        .getOrderList(currentPage, pageSize, search)
        .then((res) => res.data),
  });

  const statusMapping = {
    WAITING_FOR_APPROVE: {
      text: t("status.waiting-for-approve"),
      color: "gray",
      buttonText: t("actions.confirm"),
    },
    IN_PROGRESS: {
      text: t("status.in-progress"),
      color: "orange",
      buttonText: t("actions.finish"),
    },
    DONE: {
      text: t("status.done"),
      color: "green",
    },
    CANCELED: {
      text: t("status.canceled"),
      color: "red",
    },
  };

  useLayoutEffect(() => {
    const search = searchParams.get("search");
    if (search) {
      setSearchParams({ page: 1, pageSize: pageSize, search: search });
    } else {
      // setSearchParams({ page: , pageSize: pageSize });
    }
  }, [currentPage, pageSize, setSearchParams, search, searchParams]);

  useEffect(() => {
    if (data?.data?.length === 0 && currentPage > 1) {
      setSearchParams({ page: 1, pageSize: pageSize });
    }
  }, [data, currentPage, pageSize, setSearchParams]);
  if (error) return <p>Error :(</p>;

  const handleNavigate = (page, id) => {
    switch (page) {
      case "edit":
        navigate("/storage/order/edit/" + id);
        break;
      case "delete":
        setId(id);
        setDeleteModalVisible(true);

        break;
      case "detail":
        navigate("/storage/order/detail/" + id);
    }
  };

  const handleUpdateStatus = async (id, status) => {
    const statusIndex = Object.keys(statusMapping).indexOf(status);
    const nextStatus = Object.keys(statusMapping)[statusIndex + 1];
    try {
      const nextPayload = status === "CANCELLED" ? "CANCELLED" : nextStatus;
      const response = await services.updateOrderStatus(id, nextPayload);
      if (response.success) {
        toast.success(
          response?.data?.message
            ? response.data.message
            : t("notifications.success.order-status-update")
        );
        refetchOrders();
      } else {
        toast.error(
          response?.data?.message
            ? response.data.message
            : t("notifications.error.message")
        );
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message
          ? error.response.data.message
          : t("notifications.error.message")
      );
    }
  };

  const handleDelete = async (id) => {
    await services.deleteStorage(id).then((res) => {
      if (res.success) {
        toast.success(
          res?.message ? res.message : t("notifications.success.order-delete")
        );

        setDeleteModalVisible(false);
        refetchOrders();
      } else {
        toast.error(
          res?.message ? res.message : t("notifications.error.message")
        );
      }
    });
  };

  const handlePageChange = (page) => {
    const search = searchParams.get("search");
    if (data.data.length === 0 && page > 1) {
      setCurrentPage(page - 1);
    }

    setCurrentPage(page);
    if (search) {
      setSearchParams({ page: page, pageSize: pageSize, search: search });
      return;
    } else {
      setSearchParams({ page: page, pageSize: pageSize });
      return;
    }
  };

  const handleSearch = (value) => {
    setSearch(value);
    setSearchParams({ page: 1, pageSize: pageSize, search: value });
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <ConfirmModal
        isOpen={deleteModalVisible}
        onConfirm={() => handleDelete(id)}
        onClose={() => {
          setDeleteModalVisible(false);
          setId(null);
        }}
        title={t("titles.delete-order")}
        confirmText={t("descriptions.delete-order")}
      />
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 dark:text-white text-slate-700">
            {t("titles.storage-orders")}
          </h1>
          <p className="mt-2 text-sm dark:text-gray-400 text-gray-500">
            {t("descriptions.storage-orders")}
          </p>
        </div>

        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none flex flex-col sm:flex-row xs:flex-col-reverse gap-2 xs:justify-between justify-start items-center">
          <div className="block md:hidden xs:w-full">
            <CustomSearch
              textInputClassName="xs:w-full"
              search={search}
              onSearch={handleSearch}
            />
          </div>
          {/* <button
            type="button"
            onClick={() => handleNavigate("create")}
            className="block rounded-md bg-orange-600 dark:bg-orange-800 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-orange focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600 xs:w-full"
          >
            {t("actions.add-order")}
          </button> */}
        </div>
      </div>
      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="dark:bg-gray-800 bg-gray-300 text-slate-700">
                  <tr>
                    <th
                      scope="col"
                      className="px-3.5 py-3.5 text-left text-sm font-semibold dark:text-white text-slate-700"
                    >
                      ID
                    </th>
                    <th
                      scope="col"
                      className="px-3.5 py-3.5 text-left text-sm font-semibold dark:text-white text-slate-700"
                    >
                      {t("sales.sub-sales-area")}
                    </th>
                    <th
                      scope="col"
                      className="px-3.5 py-3.5 text-left text-sm font-semibold dark:text-white text-slate-700"
                    >
                      {t("storage.name")}
                    </th>

                    <th
                      scope="col"
                      className="px-3.5 py-3.5 text-left text-sm font-semibold dark:text-white text-slate-700"
                    >
                      {t("salespot.count")}
                    </th>
                    <th
                      scope="col"
                      className="px-3.5 py-3.5 text-left text-sm font-semibold dark:text-white text-slate-700"
                    >
                      {t("labels.status")}
                    </th>

                    <th
                      scope="col"
                      className="flex py-3.5 pl-3 pr-4 sm:pr-6 w-full "
                    >
                      <div className="hidden md:block">
                        <CustomSearch search={search} onSearch={handleSearch} />
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:bg-gray-800 bg-gray-200 text-slate-700">
                  {isLoading || isFetching ? (
                    <TableSkeleton colSize={7} />
                  ) : (
                    data.data.map((order, index) => (
                      <tr
                        key={`${order.name}-${index.toString()}`}
                        className={`${
                          index % 2 === 0
                            ? "dark:bg-slate-700/80 bg-gray-200"
                            : "dark:bg-slate-600/80 bg-gray-100"
                        } hover:bg-orange-200/90 dark:hover:bg-orange-600/20`}
                      >
                        <td className="whitespace-nowrap px-5 py-4 text-sm text-slate-700 dark:text-white">
                          {order._id}
                        </td>
                        <td className="whitespace-nowrap px-5 py-4 text-sm text-slate-700 dark:text-white">
                          {order.sale_area?.name}
                        </td>
                        <td className="whitespace-nowrap px-5 py-4 text-sm text-slate-700 dark:text-white">
                          {order.storage.name}
                        </td>

                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-300">
                          {order.sale_spot_count}
                        </td>

                        <td className="whitespace-nowrap px-3 py-4  text-sm text-gray-500 dark:text-gray-300">
                          <Badge
                            color={statusMapping[order.status].color || "gray"}
                          >
                            {statusMapping[order.status].text}
                          </Badge>
                        </td>

                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium w-[150px] sm:pr-6 dark:text-gray-300">
                          <button
                            onClick={() => handleNavigate("detail", order._id)}
                            className="text-orange-600 hover:text-orange-900 ml-3"
                          >
                            {t("actions.detail")}
                            <span className="sr-only">, {order.name}</span>
                          </button>
                          {order.status == "WAITING_FOR_APPROVE" ||
                          order.status == "IN_PROGRESS" ? (
                            <button
                              onClick={() =>
                                handleUpdateStatus(order._id, order.status)
                              }
                              className="text-orange-600 hover:text-orange-900 ml-3"
                            >
                              {statusMapping[order.status].buttonText}
                              <span className="sr-only">, {order.name}</span>
                            </button>
                          ) : null}

                          <button
                            onClick={() =>
                              handleUpdateStatus(order._id, "CANCELLED")
                            }
                            className="text-orange-600 hover:text-orange-900 ml-3"
                          >
                            {t("actions.delete")}
                            <span className="sr-only">, {order.name}</span>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-5  overflow-x-auto w-full  sm:gap-1  sm:flex-row items-center  my-5">
          <div className="flex justify-center w-full items-center">
            <Pagination
              total={data?.totalPageCount}
              siblings={1}
              size={"md"}
              defaultValue={
                Number(searchParams.get("page"))
                  ? Number(searchParams.get("page"))
                  : 1
              }
              value={currentPage}
              onChange={handlePageChange}
              color="orange"
              classNames={{
                control:
                  "flex items-center justify-center gap-x-2 dark:disabled:!bg-gray-700/50  dark:[&[data-active=true]]:!bg-orange-700/50 dark:[&[data-active=true]]:!border-orange-700  dark:[&[data-active=true]]:!border-orange-700 [&[data-active=true]]:!bg-orange-600 dark:bg-orange-800/90 [&[data-active=true]]:!border-orange-300/90 !bg-gray-200/90 !border   dark:!bg-slate-800 dark:!text-white  :disabled:!bg-gray-100/50 !rounded-lg ",
              }}
            />
          </div>
          <div className=" flex justify-end w-[80px]">
            <CustomSelect
              withoutController={true}
              placeholder={t("placeholders.select")}
              onChange={(value) => {
                setPageSize(value);
                setSearchParams({ page: 1, pageSize: value || 10 });
                setCurrentPage(1);
              }}
              data={[5, 10, 20, 30, 50]}
              value={pageSize}
              name="pageSize"
              searchable={false}
              height={"20px"}
              width={20}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StorageOrders;
